import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Path, useWatch } from 'react-hook-form';

import { Divider } from '@/components/Divider';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';
import { EntityKind } from '@/types/schema';

import { useEntityDetailsContext } from '../contexts/entityDetails/entityDetails.context';
import { AccessParameterLimitedForm } from './AccessParameterLimitedForm';
import { ACCESS_PARAMETER_RADIO_GROUP_KINDS } from './EntityBeneficiariesSubform.constants';
import {
  AccessParameterRadioGroupKind,
  BeneficiaryFormAccessParameters,
} from './EntityBeneficiariesSubform.types';
import { ScheduledDistributionsForm } from './ScheduledDistributionsForm';

type AccessParametersFormPaths = Path<BeneficiaryFormAccessParameters>;

interface AccessParametersFormProps {
  beneficiaryFieldName: 'beneficiaryFormAccessParameters';
  scheduledDistributionsFieldName: 'beneficiaryFormScheduledDistributions';
  beneficiaryId: string;
}

export function AccessParametersForm({
  beneficiaryFieldName,
  scheduledDistributionsFieldName,
  beneficiaryId,
}: AccessParametersFormProps) {
  const { control, setValue } =
    useFormContext<BeneficiaryFormAccessParameters>();

  const { entityKind } = useEntityDetailsContext();
  const isRetirement = entityKind === EntityKind.RetirementPersonalAccount;

  const [selectedAccessParameterKind] = useWatch({
    control,
    name: [
      `${beneficiaryFieldName}._accessParameterKind` as '_accessParameterKind' satisfies AccessParametersFormPaths,
    ],
  });

  useEffect(() => {
    if (
      isRetirement &&
      selectedAccessParameterKind !== AccessParameterRadioGroupKind.Full
    ) {
      setValue(
        `${beneficiaryFieldName}._accessParameterKind` as '_accessParameterKind' satisfies AccessParametersFormPaths,
        AccessParameterRadioGroupKind.Full
      );
    }
  }, [
    isRetirement,
    selectedAccessParameterKind,
    setValue,
    beneficiaryFieldName,
  ]);

  const effectiveKind = isRetirement
    ? AccessParameterRadioGroupKind.Full
    : selectedAccessParameterKind;

  return (
    <Stack>
      <Card
        variant="filled"
        sx={{
          p: 2,
          pb: 3,
          borderRadius: '6px',
        }}
      >
        <Typography
          sx={{
            pb: 2,
          }}
          variant="title-s"
        >
          Access parameters
        </Typography>
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareRadioGroup<BeneficiaryFormAccessParameters>
              control={control}
              row
              disabled={isRetirement}
              fieldName={
                `${beneficiaryFieldName}._accessParameterKind` as '_accessParameterKind' satisfies AccessParametersFormPaths
              }
              options={ACCESS_PARAMETER_RADIO_GROUP_KINDS}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <Divider
          variant="fullWidth"
          sx={{
            backgroundColor: COLORS.GRAY[300],
            borderColor: COLORS.GRAY[300],
            my: 2,
          }}
        />
        {effectiveKind === AccessParameterRadioGroupKind.Partial && (
          <AccessParameterLimitedForm
            fieldName={beneficiaryFieldName}
            beneficiaryId={beneficiaryId}
          />
        )}
        {effectiveKind === AccessParameterRadioGroupKind.Full && (
          <ScheduledDistributionsForm
            fieldName={scheduledDistributionsFieldName}
            beneficiaryId={beneficiaryId}
          />
        )}
      </Card>
    </Stack>
  );
}
